import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApproverSelectDialogComponent } from '../dialog/approver-select-dialog/approver-select-dialog.component';
import { Constant } from '../constant';
import { ToastUtil } from '../util/toast-util';
import {
  EventOperationApiService,
  EventOperationManager,
  EventOperationSettings,
} from './event-operation-api.service';

@Component({
  selector: 'app-event-operation-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatCheckboxModule, MatDialogModule, MatProgressSpinnerModule],
  templateUrl: './event-operation-settings.component.html',
  styleUrl: './event-operation-settings.component.css',
})
export class EventOperationSettingsComponent implements OnChanges {
  @Input({ required: true }) eventId = '';
  @Input() planStatus = 'draft';
  @Input() participantCategory: number | null = null;
  @Input() presentation: 'edit' | 'preview' = 'edit';
  @Output() settingsSaved = new EventEmitter<EventOperationSettings>();

  loading = true;
  saving = false;
  loadError = '';
  settings: EventOperationSettings | null = null;
  reason = '';
  editing = false;
  private savedValue = '';
  private loadPromise: Promise<void> | null = null;

  constructor(
    private api: EventOperationApiService,
    private dialog: MatDialog,
    private toastUtil: ToastUtil,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['presentation']) this.editing = this.presentation === 'edit';
    if (changes['eventId'] && this.validEventId) this.loadPromise = this.load();
  }

  get validEventId(): number {
    const id = Number(this.eventId);
    return Number.isSafeInteger(id) && id > 0 ? id : 0;
  }

  get canConfigure(): boolean {
    return this.settings?.capabilities?.canConfigure === true;
  }

  get canEdit(): boolean {
    return this.canConfigure && (this.presentation === 'edit' || this.editing);
  }

  get managerRowVisible(): boolean {
    return !!this.settings && (this.settings.attendanceSelected || this.settings.surveySelected);
  }

  get hasPendingChanges(): boolean {
    return !!this.settings && this.settings.featuresVisible !== false && this.valueOf(this.settings, true) !== this.savedValue;
  }

  get reasonRequired(): boolean {
    return this.effectivePlanStatus === 'completed';
  }

  get effectivePlanStatus(): string {
    return this.settings?.capabilities?.planStatus || this.planStatus;
  }

  get managerSummary(): string {
    const managers = this.settings?.managers ?? [];
    if (!managers.length) return '追加されている担当者はいません。';
    return managers.map(manager => {
      const person = [manager.position, manager.name].filter(Boolean).join(' ');
      const affiliation = manager.affiliations?.filter(Boolean).join('／');
      return affiliation ? `${person}（${affiliation}）` : person;
    }).join('、');
  }

  get attendanceDisabled(): boolean {
    if (!this.canEdit || !this.settings) return true;
    return !this.settings.attendanceSelected && this.settings.releaseControls?.attendance?.canWrite !== true;
  }

  get surveyDisabled(): boolean {
    if (!this.canEdit || !this.settings) return true;
    return !this.settings.surveySelected && this.settings.releaseControls?.survey?.canWrite !== true;
  }

  async load(): Promise<void> {
    this.loading = true;
    this.loadError = '';
    try {
      this.settings = await this.api.get(this.validEventId);
      this.savedValue = this.valueOf(this.settings);
    } catch (error: any) {
      this.loadError = error?.message || 'NEST機能の設定を読み込めませんでした。';
    } finally {
      this.loading = false;
    }
  }

  openManagerDialog(): void {
    if (!this.canEdit || !this.settings) return;
    const ref = this.dialog.open(ApproverSelectDialogComponent, {
      width: '760px',
      height: '664px',
      autoFocus: false,
      disableClose: true,
      data: {
        operationManagerMode: true,
        dialogTitle: '運営担当者を追加',
        eventId: this.eventId,
        documentType: 'plan',
        selectedApprovers: this.settings.managers,
      },
    });
    ref.afterClosed().subscribe(result => {
      if (Array.isArray(result) && this.settings) {
        this.settings.managers = result as EventOperationManager[];
      }
    });
  }

  removeManager(id: string): void {
    if (!this.canEdit || !this.settings) return;
    this.settings.managers = this.settings.managers.filter(manager => manager.id !== id);
  }

  startEditing(): void {
    if (this.canConfigure) this.editing = true;
  }

  async cancelEditing(): Promise<void> {
    await this.load();
    this.editing = false;
  }

  async saveForPlan(): Promise<void> {
    if (this.loadPromise) await this.loadPromise;
    if (!this.settings || this.loadError) {
      throw new Error(this.loadError || 'NEST機能の設定を読み込めませんでした。');
    }
    if (this.settings.featuresVisible === false || !this.hasPendingChanges) return;
    await this.persist();
  }

  async save(): Promise<void> {
    if (!this.settings || !this.canEdit || this.saving) return;
    if (this.reasonRequired && !this.reason.trim()) {
      this.toastUtil.showErrorToast('', '変更理由を入力してください。', Constant.toastShowMiliSecErr);
      return;
    }
    this.saving = true;
    try {
      await this.persist();
      if (this.presentation === 'preview') this.editing = false;
      this.toastUtil.showInformationToast('', 'NEST機能の設定を保存しました。', Constant.toastShowMiliSec);
    } catch (error: any) {
      this.toastUtil.showErrorToast('', error?.message || Constant.msgSaveFailedRetry, Constant.toastShowMiliSecErr);
    } finally {
      this.saving = false;
    }
  }

  private async persist(): Promise<void> {
    if (!this.settings) return;
    this.settings = await this.api.save(
      this.validEventId,
      this.settings,
      this.settings.managers.map(manager => manager.id),
      this.reason.trim(),
    );
    this.savedValue = this.valueOf(this.settings);
    this.reason = '';
    this.settingsSaved.emit(this.settings);
  }

  private valueOf(settings: EventOperationSettings, useCurrentPlan = false): string {
    return JSON.stringify({
      attendanceSelected: settings.attendanceSelected,
      surveySelected: settings.surveySelected,
      managerIds: settings.managers.map(manager => manager.id).sort(),
      participantCategory: settings.attendanceSelected
        ? (useCurrentPlan ? this.participantCategory : settings.sourceParticipantCategory)
        : null,
    });
  }
}
